import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HorseCard({ horse, t }) {
  const formatPrice = (price) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`horse-card-${horse.id}`}
    >
      <Link
        to={`/partners/${horse.id}`}
        className="group block bg-[#121212] border border-zinc-800/50 rounded-md overflow-hidden hover:border-[#ED2227]/30 transition-all duration-300"
        data-testid={`horse-card-link-${horse.id}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={horse.images?.[0] || 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=600'}
          alt={horse.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          {horse.featured && (
            <Badge className="bg-[#ED2227] text-white text-[10px] tracking-widest uppercase border-0 rounded-sm px-2 py-1">
              Featured
            </Badge>
          )}
          <Badge className="bg-black/60 backdrop-blur-sm text-white text-[10px] tracking-widest uppercase border-0 rounded-sm px-2 py-1">
            {horse.category === 'trailer' ? t('partners.trailers') : t('partners.horses')}
          </Badge>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <h3 className="text-white font-bold text-base font-[Chivo] tracking-tight">{horse.name}</h3>
        {horse.description && (
          <p className="text-zinc-500 text-xs leading-relaxed line-clamp-2">
            {horse.description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
          </p>
        )}
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/5">
          {horse.category !== 'trailer' && horse.breed && horse.breed !== 'N/A' && (
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block">{t('partners.breed')}</span>
              <span className="text-sm text-white font-medium">{horse.breed}</span>
            </div>
          )}
          {horse.category !== 'trailer' && horse.age > 0 && (
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block">{t('partners.age')}</span>
              <span className="text-sm text-white font-medium">{horse.age} yrs</span>
            </div>
          )}
          <div className={horse.category === 'trailer' ? 'col-span-3 text-right' : 'text-right'}>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block">{t('partners.price')}</span>
            <span className="text-sm text-[#ED2227] font-bold">{formatPrice(horse.price)}</span>
          </div>
        </div>
      </div>
      </Link>
    </motion.div>
  );
}

export default function Partners() {
  const { t } = useLanguage();
  const [horses, setHorses] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHorses = async () => {
      try {
        const res = await axios.get(`${API}/horses`);
        setHorses(res.data.horses || []);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchHorses();
  }, []);

  const filtered = filter === 'all'
    ? horses
    : horses.filter((h) => h.category === filter);

  const categories = ['all', 'horse', 'trailer'];

  return (
    <div className="min-h-screen pt-28 md:pt-32 pb-24" data-testid="partners-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block mb-3">
            Partners4Cars Group
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-white font-[Chivo]">
            {t('partners.title')}
          </h1>
          <p className="text-base md:text-lg text-zinc-500 mt-4 max-w-2xl">
            {t('partners.subtitle')}
          </p>
        </div>

        {/* Partners4Horses Section */}
        <div className="mb-20">
          <div className="bg-[#121212] border border-zinc-800/50 rounded-md overflow-hidden mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-5 relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_global-auto-export-2/artifacts/pod5e920_WhatsApp%20Image%202026-05-02%20at%2022.04.11.jpeg"
                  alt="Partners4Cars equestrian sponsorship"
                  className="w-full h-full object-cover min-h-[300px]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#121212] hidden lg:block" />
              </div>
              <div className="lg:col-span-7 p-8 md:p-12 flex flex-col justify-center">
                <div className="mb-8">
                  <img
                    src="https://customer-assets.emergentagent.com/job_global-auto-export-2/artifacts/tqbtuwsi_partners4horses_white.png"
                    alt="Partners4Horses Logo"
                    className="h-20 md:h-24 w-auto object-contain"
                    data-testid="p4h-logo"
                  />
                </div>
                <p className="text-zinc-400 text-base leading-relaxed mb-8">
                  {t('partners.p4hDescription')}
                </p>
                <div className="grid grid-cols-3 gap-6">
                  {[
                    { value: `${horses.filter(h => h.category === 'horse').length}+`, label: t('partners.horses') },
                    { value: `${horses.filter(h => h.category === 'trailer').length}+`, label: t('partners.trailers') },
                    { value: '10+', label: 'Years' }
                  ].map((stat, i) => (
                    <div key={i}>
                      <span className="text-2xl font-black text-[#ED2227] font-[Chivo] block">{stat.value}</span>
                      <span className="text-xs font-mono uppercase tracking-widest text-zinc-600">{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-4 mb-8 border-b border-white/5 pb-4" data-testid="horse-filters">
            {categories.map((cat) => (
              <Button
                key={cat}
                variant="ghost"
                onClick={() => setFilter(cat)}
                data-testid={`horse-filter-${cat}`}
                className={`text-xs uppercase tracking-[0.2em] rounded-sm px-4 h-9 ${
                  filter === cat ? 'text-white bg-white/5' : 'text-zinc-600 hover:text-white'
                }`}
              >
                {cat === 'all' ? t('partners.all') : cat === 'horse' ? t('partners.horses') : t('partners.trailers')}
              </Button>
            ))}
          </div>

          {/* Horse Inventory Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-[#121212] border border-zinc-800/50 rounded-md animate-pulse">
                  <div className="aspect-[4/3] bg-zinc-800/50" />
                  <div className="p-4 space-y-3"><div className="h-5 bg-zinc-800/50 rounded w-3/4" /></div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-zinc-500 text-lg">{t('partners.noResults')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((horse) => (
                <HorseCard key={horse.id} horse={horse} t={t} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
